<template>
  <v-app>
    <v-app-bar
      v-if="user"
      app
      class="mr-3 pt-1 px-4"
      :color="isDarkMode ? '' : 'primary'"
      >
      <v-app-bar-nav-icon
        icon="mdi-chart-donut-variant"
        variant="text"
        rounded="true"
        color="white"
        to="/polls"
      />
        <v-app-bar-title text="Polling Portal" />
      <v-spacer></v-spacer>
      <v-menu offset-y offset="10" width="300">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"

            large
            text
            class="pa-2"
            height="50"
            color="white"
            >
            <span style="color: white !important;" class="text-body-2">{{ workspace.name }}</span>
            <v-avatar
              size="35"
              class="ml-2"
              :color="initials.color">
              <span class="text-white">{{ initials.initials }}</span>
            </v-avatar>
          </v-btn>
        </template>
        <v-list nav>
          <v-list-item>
            <template v-slot:prepend>
            <v-avatar
              class="mx-auto"
              :color="initials.color">
              <span class="text-white">{{ initials.initials }}</span>
            </v-avatar>
          </template>
            <v-list-item-title>
              {{ user.name }}
            </v-list-item-title>
            <v-list-item-subtitle>
              {{ user.email }}
            </v-list-item-subtitle>
          </v-list-item>
          <v-divider class="my-2" />
          <v-list-item
            prepend-icon="mdi-theme-light-dark"
          >
            <v-list-item-title class="d-flex align-center justify-space-between">
              Dark Mode
            </v-list-item-title>
            <template v-slot:append>
                <v-switch
                color="primary"
                v-model="isDarkMode"
                hide-details
                density="compact"
                @update:model-value="toggleTheme"
                @click.stop
              ></v-switch>
            </template>
          </v-list-item>
          <v-divider v-if="isPrincipal" class="my-2" />
          <v-list-item
            v-if="isPrincipal"
            to="/admin/overview"
            prepend-icon="mdi-view-dashboard"
          >
            <v-list-item-title>Admin Overview</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="isPrincipal"
             to="/files/combiner"
             prepend-icon="mdi-file-document-plus"
          >
            <v-list-item-title>File Combiner</v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="isPrincipal"
            to="/files/normalise"
            prepend-icon="mdi-file-multiple"
          >
            <v-list-item-title>File Normaliser</v-list-item-title>
          </v-list-item>
          <v-divider class="my-2" />
          <v-list-item
            @click="changeWorkspace"
            prepend-icon="mdi-domain"
          >
            <v-list-item-title>Change Workspace</v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="signOut"
            prepend-icon="mdi-logout"
          >
            <v-list-item-title>Logout</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>
    <v-main class="mx-4 mx-s-0">
      <router-view/>
    </v-main>
    <v-snackbar
      v-model="snackbar.visible"
      :timeout="snackbar.timeout"
      :variant="snackbar.variant"
      :color="snackbar.color">
      {{ snackbar.message }}
      <template #actions>
        <v-btn
          :color="snackbar.closeColor"
          variant="text"
          @click="closeSnackbar">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script>
import { toRefs, ref, watch, onMounted } from 'vue';
import { useTheme } from 'vuetify';
import get from 'lodash/get';

import { mapActions, mapState } from 'redux-vuex';

import { isPrincipal, memberInitials } from './selectors';

import {
  getWorkspaceId,
  getChangeWorkspaceLink,
  getYabbrLink,
} from './utils';

export default {
  name: 'App',
  setup() {
    const theme = useTheme();
    const isDarkMode = ref(theme.global.current.value.dark);

    const toggleTheme = () => {
      theme.global.name.value = isDarkMode.value ? 'dark' : 'light';
    };

    onMounted(() => {
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        isDarkMode.value = savedTheme === 'dark';
        theme.global.name.value = savedTheme;
      }
    });

    watch(isDarkMode, (newValue) => {
      localStorage.setItem('theme', newValue ? 'dark' : 'light');
    });

    return {
      isDarkMode,
      toggleTheme,
      ...mapActions(
        'endSession',
        'setSnackbar',
        'closeSnackbar',
      ),
      ...toRefs(mapState(
        'breadcrumbs',
        'snackbar',
        'user',
        'workspaceMemberList',
        'workspaceDetail',
      )),
      ...toRefs(mapState({
        memberInitials,
        isPrincipal,
      })),
    };
  },
  methods: {
    changeWorkspace() {
      localStorage.removeItem('yabbr-organisation');
      window.location.href = getChangeWorkspaceLink();
    },
    signOut() {
      localStorage.removeItem('yabbr-token');
      this.endSession();
      window.location.href = getYabbrLink();
    },
    hasPermission(perms) {
      if (!this.memberList.resources[this.state?.user?.id]) return false;

      return perms.indexOf(this.memberList.resources[this.state?.user?.id] !== -1);
    },
  },
  computed: {
    workspace() {
      return get(this.workspaceDetail, ['resources', getWorkspaceId()], {});
    },
    initials() {
      if (!this.user || !this.user.id || !(this.user.id in this.memberInitials)) return {
        color: '',
        initials: '',
      };
      return this.memberInitials[this.user.id];
    },
  },
};
</script>

<style lang="scss">
body {
  font-family: Open Sans,Roboto,monospace !important;
}

.cursor-pointer {
  cursor: pointer;
}

// .theme--light.v-app-bar.v-toolbar.v-sheet {
//   background: none !important;
// }

.theme--light.v-toolbar.v-sheet {
  background: none !important;
}

.theme--light.v-breadcrumbs .v-breadcrumbs__item--disabled {
  color: rgba(0, 0, 0, 0.87) !important;
}

.theme--light.v-application {
  background: white !important;
}

.v-sheet.v-card.elevation-2 {
  border-radius: 8px !important;
  box-shadow: 0 5px 8px rgb(52 73 82 / 10%) !important;
}

/* eslint max-len: "off" */
.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  cursor: pointer;
}

.data-table-container table {
  // width: auto !important;
  // tr {
  //   &> td:first-child,
  //   &> th:first-child {
  //     border-right: 1px solid #dddddd;
  //   }
  // }
}

html {
  overflow-y: auto !important;
}

.ql-toolbar.ql-snow {
  border-top-right-radius: 4px;
  border-top-left-radius: 4px;
}
.ql-container.ql-snow {
  border-bottom-right-radius: 4px;
  border-bottom-left-radius: 4px;
  }

  .ql-editor.ql-blank::before {
    color: grey !important;
    font-style: normal !important;
  }

  blockquote {
  border-left: 4px solid #ccc;
  margin: 0;
  padding-left: 16px;
  color: #666;
}
</style>
